// PERFORMANCE OPTIMIZED Employee Service Implementation - frontend/src/services/employee.service.ts
// Removed all console.log statements for better INP scores
// Added enhanced caching patterns

import { 
  collection, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  getDoc,
  setDoc,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Unsubscribe,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';

export interface Employee {
  id: string;
  name: string;
  employeeNumber: string;
  designation: string;
  department: string;
  email?: string;
  phone?: string;
  joinDate?: string;
  status: 'Active' | 'Inactive' | 'On Leave';
  avatar?: string;
  pin?: string;
  salary?: number;
  passportNumber?: string;
  passportExpiry?: string;
  workEmail?: string;
  'Emirates ID No.'?: string;
  'Emirates ID Expiry Date'?: string;
  'Residency Visa Expiry Date'?: string;
  'Visa Expiry Date'?: string;
  'Personal No.'?: string;
  'Work Permit No.'?: string;
  personalNumber?: string;
  workPermitNumber?: string;
  labourCardIssueDate?: string;
  employerLicense?: string;
  workLocation?: string;
  passportIssueDate?: string;
  passportPlaceOfIssue?: string;
  'Residency Visa Issue Date'?: string;
  'Residency Visa Entry Date'?: string;
  sponsorId?: string;
  profession?: string;
  cardNumber?: string;
  arabicName?: string;
  placeOfBirth?: string;
  overtime?: string;
  hasOvertime?: boolean;
  hasOvertimeAccess?: boolean;
  createdBy?: string;
  createdOn?: any;
  overtimeUpdatedAt?: any;
  overtimeAccessGrantedAt?: any;
  overtimeAccessRevokedAt?: any;
  preferredName?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  nationality?: string;
  gender?: string;
  mobileNo?: string;
  workNo?: string;
  title?: string;
  hiringDate?: string;
  probationEndDate?: string;
  workWeek?: string;
  legalCountryOfResidence?: string;
  workTiming?: string;
  lineManagerId?: string;
  lineManagerName?: string;
  lineManagerDepartment?: string;
  currentlyInsured?: boolean;
  sendInvitation?: boolean;
  [key: string]: any;
}

export interface EmployeeFilters {
  searchQuery?: string;
  designation?: string;
  page?: number;
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
}

export interface EmployeeStats {
  totalEmployees: number;
  activeEmployees: number;
  inactiveEmployees: number;
  onLeaveEmployees: number;
  totalDepartments: number;
  totalDesignations: number;
}

export interface MonthlyUpdateInfo {
  month: string;
  year: number;
  updatesThisMonth: number;
  maxUpdatesPerMonth: number;
  lastUpdateDate: string | null;
  canUpdate: boolean;
}

export interface MonthlyUpdateRecord {
  id: string;
  month: string;
  year: number;
  updateType: 'import' | 'manual';
  status: string;
  timestamp: string;
  updatedBy: string;
  employeesAffected: number;
  description: string;
}

export interface ImportConfig {
  file: File;
  updateExisting?: boolean;
  validateOnly?: boolean;
  monthlyUpdate?: {
    month: string;
    year: number;
    status: string;
    timestamp: string;
  };
}

export interface ImportResult {
  success: boolean;
  totalRows: number;
  processedRows: number;
  errors: ImportError[];
  warnings: ImportWarning[];
  newEmployees: Employee[];
  updatedEmployees: Employee[];
}

export interface ImportError {
  row: number;
  field?: string;
  message: string;
  data?: any;
}

export interface ImportWarning {
  row: number;
  field?: string;
  message: string;
  data?: any;
}

export interface ImportProgress {
  current: number;
  total: number;
  status: 'reading' | 'validating' | 'processing' | 'saving' | 'complete' | 'error';
  message: string;
}

export interface DesignationGroup {
  designation: string;
  count: number;
  employees: Employee[];
}

export interface DesignationSummary {
  designation: string;
  count: number;
}

// Static constants moved outside class for performance
const REQUIRED_COLUMNS = [
  'employeename', 'employeenumber', 'designation', 'hiredat'
];

const OPTIONAL_COLUMNS = [
  'passportnumber', 'passportexpiry', 'workemail', 'phone', 'email'
];

const COLUMN_MAPPING: Record<string, string> = {
  'employeename': 'name',
  'employee name': 'name',
  'name': 'name',
  'employeenumber': 'employeeNumber',
  'employee number': 'employeeNumber',
  'empno': 'employeeNumber',
  'designation': 'designation',
  'position': 'designation',
  'title': 'designation',
  'hiredat': 'joinDate',
  'hired at': 'joinDate',
  'join date': 'joinDate',
  'joining date': 'joinDate',
  'passportnumber': 'passportNumber',
  'passport number': 'passportNumber',
  'passportexpiry': 'passportExpiry',
  'passport expiry': 'passportExpiry',
  'passport expiry date': 'passportExpiry',
  'workemail': 'workEmail',
  'work email': 'workEmail',
  'email': 'email',
  'phone': 'phone',
  'phonenumber': 'phone',
  'phone number': 'phone'
};

// Fields to exclude from mapping
const EXCLUDED_MAPPING_FIELDS = new Set([
  'employeeName', 'name', 'employeeNumber', 'designation', 'title', 
  'email', 'phone', 'mobileNo', 'phoneNumber', 'joinDate', 'hiringDate', 
  'joiningDate', 'createdOn', 'salary', 'passportNumber', 'passportExpiry', 
  'workEmail', 'overtime', 'hasOvertime', 'hasOvertimeAccess', 'preferredName', 
  'firstName', 'lastName', 'dateOfBirth', 'nationality', 'gender', 'workNo', 
  'probationEndDate', 'workWeek', 'legalCountryOfResidence', 'workTiming', 
  'lineManagerId', 'lineManagerName', 'lineManagerDepartment', 'currentlyInsured', 
  'sendInvitation'
]);

// Fields to exclude from save operations
const EXCLUDED_SAVE_FIELDS = new Set([
  'id', 'name', 'employeeNumber', 'department', 'status', 'avatar', 'pin', 'salary'
]);

class EmployeeService {
  private sessionCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private activeListeners = new Set<Unsubscribe>();

  // Firebase safe limits
  private readonly MAX_FIREBASE_LIMIT = 9999;
  private readonly BATCH_SIZE = 1000;

  // Enhanced cache for frequently accessed data
  private _statsCache: { data: EmployeeStats; timestamp: number } | null = null;
  private _designationsCache: { data: string[]; timestamp: number } | null = null;
  private _lineManagersCache: { data: any[]; timestamp: number } | null = null;

  createLineManager(managerData: { managerId: string; managerEmployeeNumber: string; managerName: string; department: string; teamMembers: string[]; managerPin: string; createdAt: Date; createdBy: string; }) {
    throw new Error('Method not implemented.');
  }

  deleteLineManager(managerId: string) {
    throw new Error('Method not implemented.');
  }

  // =====================
  // HELPER METHODS
  // =====================

  private getFromCache<T>(key: string): T | null {
    const cached = this.sessionCache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      return cached.data;
    }
    this.sessionCache.delete(key);
    return null;
  }

  private setCache(key: string, data: any): void {
    this.sessionCache.set(key, { data, timestamp: Date.now() });
  }

  private mapEmployeeData(doc: QueryDocumentSnapshot<DocumentData>): Employee {
    const data = doc.data();
    const docId = doc.id;
    
    const employeeNumber = docId;
    const pin = data.employeeNumber || docId.replace(/^EMP/i, '');
    
    // Build additional fields more efficiently
    const additionalFields: Record<string, any> = {};
    for (const key of Object.keys(data)) {
      if (!EXCLUDED_MAPPING_FIELDS.has(key)) {
        additionalFields[key] = data[key];
      }
    }
    
    return {
      id: docId,
      name: data.employeeName || data.name || 'Unknown Employee',
      employeeNumber: employeeNumber,
      designation: data.designation || data.title || 'Not Specified',
      department: 'Not Specified',
      email: data.email || '',
      phone: data.phone || data.mobileNo || data.phoneNumber || '',
      joinDate: data.joinDate || data.hiringDate || data.joiningDate || data.createdOn || '',
      status: 'Active',
      avatar: '/images/humanicon.png',
      pin: pin,
      salary: data.salary || 0,
      passportNumber: data.passportNumber || '',
      passportExpiry: data.passportExpiry || '',
      workEmail: data.workEmail || '',
      overtime: data.overtime || 'No',
      hasOvertime: data.hasOvertime || false,
      hasOvertimeAccess: data.hasOvertimeAccess || false,
      createdBy: data.createdBy || 'Default',
      createdOn: data.createdOn || null,
      overtimeUpdatedAt: data.overtimeUpdatedAt || null,
      overtimeAccessGrantedAt: data.overtimeAccessGrantedAt || null,
      overtimeAccessRevokedAt: data.overtimeAccessRevokedAt || null,
      preferredName: data.preferredName || '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      dateOfBirth: data.dateOfBirth || '',
      nationality: data.nationality || '',
      gender: data.gender || '',
      mobileNo: data.mobileNo || data.phone || '',
      workNo: data.workNo || '',
      title: data.title || data.designation || '',
      hiringDate: data.hiringDate || data.joinDate || '',
      probationEndDate: data.probationEndDate || '',
      workWeek: data.workWeek || '',
      legalCountryOfResidence: data.legalCountryOfResidence || '',
      workTiming: data.workTiming || '',
      lineManagerId: data.lineManagerId || '',
      lineManagerName: data.lineManagerName || '',
      lineManagerDepartment: data.lineManagerDepartment || '',
      currentlyInsured: data.currentlyInsured || false,
      sendInvitation: data.sendInvitation || false,
      ...additionalFields
    };
  }

  // =====================
  // EMPLOYEE CRUD METHODS
  // =====================

  async createEmployee(employeeData: any): Promise<Employee> {
    try {
      const employeeId = employeeData.employeeNumber.startsWith('EMP') 
        ? employeeData.employeeNumber 
        : `EMP${employeeData.employeeNumber.padStart(4, '0')}`;

      const existingEmployee = await this.getEmployeeById(employeeId);
      if (existingEmployee) {
        throw new Error(`Employee with number ${employeeData.employeeNumber} already exists`);
      }

      const firestoreData = {
        employeeName: employeeData.employeeName,
        employeeNumber: employeeData.employeeNumber,
        designation: employeeData.designation,
        email: employeeData.email || '',
        phone: employeeData.phone || '',
        workEmail: employeeData.workEmail || '',
        joinDate: employeeData.joinDate || '',
        passportNumber: employeeData.passportNumber || '',
        passportExpiry: employeeData.passportExpiry || '',
        salary: employeeData.salary || 0,
        overtime: employeeData.overtime || 'No',
        hasOvertime: employeeData.hasOvertime || false,
        hasOvertimeAccess: employeeData.hasOvertimeAccess || false,
        preferredName: employeeData.preferredName || '',
        firstName: employeeData.firstName || '',
        lastName: employeeData.lastName || '',
        dateOfBirth: employeeData.dateOfBirth || '',
        nationality: employeeData.nationality || '',
        gender: employeeData.gender || '',
        mobileNo: employeeData.mobileNo || employeeData.phone || '',
        workNo: employeeData.workNo || '',
        title: employeeData.title || employeeData.designation || '',
        hiringDate: employeeData.hiringDate || employeeData.joinDate || '',
        probationEndDate: employeeData.probationEndDate || '',
        workWeek: employeeData.workWeek || 'Default',
        legalCountryOfResidence: employeeData.legalCountryOfResidence || '',
        workTiming: employeeData.workTiming || 'Default (09:00 AM - 06:00 PM)',
        lineManagerId: employeeData.lineManagerId || '',
        lineManagerName: employeeData.lineManagerName || '',
        lineManagerDepartment: employeeData.lineManagerDepartment || '',
        currentlyInsured: employeeData.currentlyInsured || false,
        sendInvitation: employeeData.sendInvitation || false,
        createdBy: employeeData.createdBy || 'Admin',
        createdOn: employeeData.createdOn || new Date(),
        overtimeUpdatedAt: employeeData.hasOvertime ? (employeeData.overtimeUpdatedAt || new Date()) : null,
        overtimeAccessGrantedAt: employeeData.hasOvertimeAccess ? (employeeData.overtimeAccessGrantedAt || new Date()) : null,
        overtimeAccessRevokedAt: employeeData.overtimeAccessRevokedAt || null
      };

      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const employeeDoc = doc(employeesRef, employeeId);
      
      await setDoc(employeeDoc, firestoreData);
      
      this.clearCache();
      
      const createdEmployee: Employee = {
        id: employeeId,
        name: employeeData.employeeName,
        employeeNumber: employeeId,
        designation: employeeData.designation,
        department: 'Not Specified',
        email: employeeData.email || '',
        phone: employeeData.phone || employeeData.mobileNo || '',
        joinDate: employeeData.joinDate || employeeData.hiringDate || '',
        status: 'Active',
        avatar: '/images/humanicon.png',
        pin: employeeData.employeeNumber,
        salary: employeeData.salary || 0,
        passportNumber: employeeData.passportNumber || '',
        passportExpiry: employeeData.passportExpiry || '',
        workEmail: employeeData.workEmail || '',
        overtime: employeeData.overtime || 'No',
        hasOvertime: employeeData.hasOvertime || false,
        hasOvertimeAccess: employeeData.hasOvertimeAccess || false,
        preferredName: employeeData.preferredName || '',
        firstName: employeeData.firstName || '',
        lastName: employeeData.lastName || '',
        dateOfBirth: employeeData.dateOfBirth || '',
        nationality: employeeData.nationality || '',
        gender: employeeData.gender || '',
        title: employeeData.title || '',
        hiringDate: employeeData.hiringDate || '',
        probationEndDate: employeeData.probationEndDate || '',
        workWeek: employeeData.workWeek || '',
        legalCountryOfResidence: employeeData.legalCountryOfResidence || '',
        workTiming: employeeData.workTiming || '',
        lineManagerId: employeeData.lineManagerId || '',
        lineManagerName: employeeData.lineManagerName || '',
        lineManagerDepartment: employeeData.lineManagerDepartment || '',
        currentlyInsured: employeeData.currentlyInsured || false,
        sendInvitation: employeeData.sendInvitation || false
      };
      
      return createdEmployee;
      
    } catch (error) {
      throw new Error(`Failed to create employee: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async updateEmployee(employeeId: string, updateData: Partial<any>): Promise<Employee> {
    try {
      if (!employeeId) {
        throw new Error('Employee ID is required for update');
      }
      
      if (!updateData || Object.keys(updateData).length === 0) {
        throw new Error('No update data provided');
      }

      const existingEmployee = await this.getEmployeeById(employeeId);
      if (!existingEmployee) {
        throw new Error(`Employee with ID ${employeeId} not found in database`);
      }

      const firestoreUpdateData: any = {};
      let validFieldCount = 0;
      
      for (const [key, value] of Object.entries(updateData)) {
        if (value !== undefined && value !== null && value !== '') {
          firestoreUpdateData[key] = value;
          if (!key.includes('UpdatedAt')) {
            validFieldCount++;
          }
        }
      }

      if (validFieldCount === 0) {
        return existingEmployee;
      }

      firestoreUpdateData.lastUpdated = new Date();
      firestoreUpdateData.updatedAt = new Date();

      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const employeeDoc = doc(employeesRef, employeeId);
      
      try {
        await setDoc(employeeDoc, firestoreUpdateData, { merge: true });
      } catch (firebaseError) {
        if (firebaseError instanceof Error) {
          const errorMessage = firebaseError.message.toLowerCase();
          
          if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
            throw new Error('Permission denied: Unable to update employee data.');
          } else if (errorMessage.includes('network') || errorMessage.includes('offline')) {
            throw new Error('Network error: Unable to connect to database.');
          } else if (errorMessage.includes('not-found')) {
            throw new Error(`Employee document ${employeeId} was not found.`);
          } else if (errorMessage.includes('quota') || errorMessage.includes('limit')) {
            throw new Error('Database quota exceeded.');
          } else {
            throw new Error(`Database update failed: ${firebaseError.message}`);
          }
        } else {
          throw new Error('Unknown Firebase error occurred during update');
        }
      }
      
      this.clearCache();
      
      const updatedEmployee = await this.getEmployeeById(employeeId);
      
      if (!updatedEmployee) {
        throw new Error('Failed to retrieve updated employee data');
      }
      
      return updatedEmployee;
      
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Employee update failed: ${error.message}`);
      } else {
        throw new Error('Unknown error occurred while updating employee data');
      }
    }
  }

  async deleteEmployee(employeeId: string, reason?: string): Promise<void> {
    try {
      const existingEmployee = await this.getEmployeeById(employeeId);
      if (!existingEmployee) {
        throw new Error(`Employee with ID ${employeeId} not found`);
      }

      const archiveRef = collection(db, 'MasterSheet', 'Employee-Data', 'archived');
      const archiveDoc = doc(archiveRef, employeeId);
      
      const archiveData = {
        ...existingEmployee,
        archivedAt: new Date(),
        archivedBy: 'Admin',
        archiveReason: reason || 'No longer with company',
        originalData: existingEmployee
      };
      
      await setDoc(archiveDoc, archiveData);
      
      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const employeeDoc = doc(employeesRef, employeeId);
      
      const batch = writeBatch(db);
      batch.set(archiveDoc, archiveData);
      batch.delete(employeeDoc);
      
      await batch.commit();
      
      this.clearCache();
      
    } catch (error) {
      throw new Error(`Failed to delete employee: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // =====================
  // MONTHLY TRACKING METHODS
  // =====================

  async getMonthlyUpdateInfo(month: string, year: number): Promise<MonthlyUpdateInfo> {
    try {
      const monthKey = `${year}-${month}`;
      const updatesRef = collection(db, 'EmployeeUpdates', 'Monthly', 'updates');
      
      const monthQuery = query(
        updatesRef,
        where('monthKey', '==', monthKey),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(monthQuery);
      const updates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as MonthlyUpdateRecord[];
      
      const updatesThisMonth = updates.length;
      const maxUpdatesPerMonth = 2;
      const canUpdate = updatesThisMonth < maxUpdatesPerMonth;
      const lastUpdateDate = updates.length > 0 ? updates[0].timestamp : null;
      
      return {
        month,
        year,
        updatesThisMonth,
        maxUpdatesPerMonth,
        lastUpdateDate,
        canUpdate
      };
      
    } catch (error) {
      return {
        month,
        year,
        updatesThisMonth: 0,
        maxUpdatesPerMonth: 2,
        lastUpdateDate: null,
        canUpdate: true
      };
    }
  }

  async recordMonthlyUpdate(
    month: string, 
    year: number, 
    status: string, 
    employeesAffected: number,
    updateType: 'import' | 'manual' = 'import'
  ): Promise<void> {
    try {
      const monthKey = `${year}-${month}`;
      const updateId = `${monthKey}_${Date.now()}`;
      
      const updateRecord: MonthlyUpdateRecord = {
        id: updateId,
        month,
        year,
        updateType,
        status,
        timestamp: new Date().toISOString(),
        updatedBy: 'current_user',
        employeesAffected,
        description: `${updateType === 'import' ? 'Import' : 'Manual'} update: ${status}`
      };
      
      const updatesRef = collection(db, 'EmployeeUpdates', 'Monthly', 'updates');
      const updateDoc = doc(updatesRef, updateId);
      
      await setDoc(updateDoc, {
        ...updateRecord,
        monthKey
      });
      
    } catch (error) {
      throw new Error(`Failed to record monthly update: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getMonthlyUpdateHistory(year?: number): Promise<MonthlyUpdateRecord[]> {
    try {
      const updatesRef = collection(db, 'EmployeeUpdates', 'Monthly', 'updates');
      
      let historyQuery;
      if (year) {
        historyQuery = query(
          updatesRef,
          where('year', '==', year),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
      } else {
        historyQuery = query(
          updatesRef,
          orderBy('timestamp', 'desc'),
          limit(50)
        );
      }
      
      const snapshot = await getDocs(historyQuery);
      return snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      })) as MonthlyUpdateRecord[];
      
    } catch (error) {
      return [];
    }
  }

  async getCurrentMonthPayrollStatus(): Promise<{
    month: string;
    year: number;
    status: string;
    lastUpdated: string | null;
    canUpdate: boolean;
  }> {
    try {
      const now = new Date();
      const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
      const currentYear = now.getFullYear();
      
      const monthlyInfo = await this.getMonthlyUpdateInfo(currentMonth, currentYear);
      
      const updatesRef = collection(db, 'EmployeeUpdates', 'Monthly', 'updates');
      const monthKey = `${currentYear}-${currentMonth}`;
      
      const latestQuery = query(
        updatesRef,
        where('monthKey', '==', monthKey),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(latestQuery);
      const latestUpdate = snapshot.docs.length > 0 ? snapshot.docs[0].data() : null;
      
      return {
        month: currentMonth,
        year: currentYear,
        status: latestUpdate?.status || 'No updates this month',
        lastUpdated: latestUpdate?.timestamp || null,
        canUpdate: monthlyInfo.canUpdate
      };
      
    } catch (error) {
      const now = new Date();
      return {
        month: String(now.getMonth() + 1).padStart(2, '0'),
        year: now.getFullYear(),
        status: 'Error loading status',
        lastUpdated: null,
        canUpdate: false
      };
    }
  }

  // =====================
  // DESIGNATION GROUPING METHODS
  // =====================

  async getDesignationSummaries(): Promise<DesignationSummary[]> {
    try {
      const cacheKey = 'designation_summaries';
      const cached = this.getFromCache<DesignationSummary[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      
      const designationCounts = new Map<string, number>();
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let hasMore = true;

      while (hasMore) {
        let batchQuery = query(
          employeesRef, 
          orderBy('__name__'),
          limit(this.BATCH_SIZE)
        );
        
        if (lastDoc) {
          batchQuery = query(batchQuery, startAfter(lastDoc));
        }

        const snapshot = await getDocs(batchQuery);
        
        if (snapshot.empty || snapshot.docs.length === 0) {
          hasMore = false;
          break;
        }

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const designation = data.designation || 'Not Specified';
          designationCounts.set(designation, (designationCounts.get(designation) || 0) + 1);
        });

        hasMore = snapshot.docs.length === this.BATCH_SIZE;
        lastDoc = snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData>;
      }

      const summaries: DesignationSummary[] = Array.from(designationCounts.entries())
        .map(([designation, count]) => ({ designation, count }))
        .sort((a, b) => b.count - a.count);

      this.setCache(cacheKey, summaries);
      
      return summaries;

    } catch (error) {
      throw new Error(`Failed to get designation summaries: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getEmployeesByDesignation(designation: string, pageSize: number = 50): Promise<Employee[]> {
    try {
      const cacheKey = `employees_by_designation_${designation}`;
      const cached = this.getFromCache<Employee[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const safeLimit = Math.min(pageSize, this.MAX_FIREBASE_LIMIT);
      
      let employees: Employee[] = [];
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let hasMore = true;

      while (hasMore && employees.length < safeLimit) {
        let employeeQuery: any;
        
        if (designation === 'Not Specified') {
          employeeQuery = query(
            employeesRef,
            orderBy('__name__'),
            limit(this.BATCH_SIZE)
          );
        } else {
          employeeQuery = query(
            employeesRef,
            where('designation', '==', designation),
            orderBy('__name__'),
            limit(safeLimit)
          );
        }

        if (lastDoc && designation === 'Not Specified') {
          employeeQuery = query(employeeQuery, startAfter(lastDoc));
        }

        const snapshot = await getDocs(employeeQuery);
        
        if (snapshot.empty || snapshot.docs.length === 0) {
          hasMore = false;
          break;
        }

        const batchEmployees = snapshot.docs
          .map(doc => this.mapEmployeeData(doc as QueryDocumentSnapshot<DocumentData>))
          .filter(emp => {
            if (designation === 'Not Specified') {
              return !emp.designation || emp.designation === 'Not Specified' || emp.designation.trim() === '';
            }
            return emp.designation === designation;
          });

        employees.push(...batchEmployees);

        if (designation !== 'Not Specified') {
          hasMore = false;
        } else {
          hasMore = snapshot.docs.length === this.BATCH_SIZE && employees.length < safeLimit;
          lastDoc = snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData>;
        }
      }

      employees.sort((a, b) => a.name.localeCompare(b.name));
      employees = employees.slice(0, safeLimit);
      
      this.setCache(cacheKey, employees);
      
      return employees;

    } catch (error) {
      throw new Error(`Failed to get employees for designation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getEmployeesByDesignationGroups(): Promise<DesignationGroup[]> {
    try {
      const summaries = await this.getDesignationSummaries();
      
      return summaries.map(summary => ({
        designation: summary.designation,
        count: summary.count,
        employees: []
      }));
      
    } catch (error) {
      throw new Error(`Failed to get designation groups: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // =====================
  // CORE EMPLOYEE METHODS
  // =====================

  async getEmployees(filters: EmployeeFilters = {}): Promise<{
    employees: Employee[];
    hasMore: boolean;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
  }> {
    try {
      const pageSize = Math.min(filters.pageSize || 50, this.MAX_FIREBASE_LIMIT);
      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      
      if (filters.designation && filters.designation !== 'all') {
        let allEmployees: Employee[] = [];
        let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
        let hasMore = true;

        if (filters.designation === 'Not Specified') {
          while (hasMore) {
            let batchQuery = query(
              employeesRef,
              orderBy('__name__'),
              limit(this.BATCH_SIZE)
            );
            
            if (lastDoc) {
              batchQuery = query(batchQuery, startAfter(lastDoc));
            }

            const snapshot = await getDocs(batchQuery);
            
            if (snapshot.empty || snapshot.docs.length === 0) {
              hasMore = false;
              break;
            }

            const batchEmployees = snapshot.docs
              .map(doc => this.mapEmployeeData(doc as QueryDocumentSnapshot<DocumentData>))
              .filter(emp => !emp.designation || emp.designation === 'Not Specified' || emp.designation.trim() === '');

            allEmployees.push(...batchEmployees);
            
            hasMore = snapshot.docs.length === this.BATCH_SIZE;
            lastDoc = snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData>;
          }
        } else {
          const designationQuery = query(
            employeesRef,
            where('designation', '==', filters.designation),
            orderBy('__name__'),
            limit(this.MAX_FIREBASE_LIMIT)
          );

          const snapshot = await getDocs(designationQuery);
          allEmployees = snapshot.docs.map(doc => this.mapEmployeeData(doc as QueryDocumentSnapshot<DocumentData>));
        }

        if (filters.searchQuery && filters.searchQuery.trim() !== '') {
          const searchTerm = filters.searchQuery.toLowerCase().trim();
          allEmployees = allEmployees.filter(emp => {
            const searchableFields = [
              emp.name.toLowerCase(),
              emp.employeeNumber.toLowerCase(),
              emp.designation.toLowerCase(),
              emp.pin?.toLowerCase() || '',
              emp.email?.toLowerCase() || '',
              emp.phone?.toLowerCase() || ''
            ];
            return searchableFields.some(field => field.includes(searchTerm));
          });
        }

        allEmployees.sort((a, b) => a.name.localeCompare(b.name));

        return {
          employees: allEmployees,
          hasMore: false,
          lastDoc: undefined
        };
      }

      const queryLimit = filters.searchQuery && filters.searchQuery.trim() !== '' 
        ? Math.min(1000, this.MAX_FIREBASE_LIMIT) 
        : pageSize + 1;
      
      let employeeQuery = query(employeesRef, limit(queryLimit));
      
      if (filters.lastDoc && (!filters.searchQuery || filters.searchQuery.trim() === '')) {
        employeeQuery = query(employeeQuery, startAfter(filters.lastDoc));
      }

      const snapshot = await getDocs(employeeQuery);
      
      if (snapshot.empty) {
        return { employees: [], hasMore: false };
      }

      let allEmployees = snapshot.docs.map(doc => {
        try {
          return this.mapEmployeeData(doc as QueryDocumentSnapshot<DocumentData>);
        } catch (mappingError) {
          return {
            id: doc.id,
            name: doc.data().employeeName || doc.data().name || 'Unknown',
            employeeNumber: doc.id,
            designation: doc.data().designation || 'Unknown',
            department: 'Not Specified',
            email: doc.data().email || '',
            phone: doc.data().phone || doc.data().phoneNumber || '',
            joinDate: doc.data().joinDate || doc.data().joiningDate || '',
            status: 'Active' as const,
            avatar: '/images/humanicon.png',
            pin: doc.id.replace(/^EMP/i, ''),
            salary: doc.data().salary || 0
          };
        }
      });

      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        const searchTerm = filters.searchQuery.toLowerCase().trim();
        allEmployees = allEmployees.filter(emp => {
          const searchableFields = [
            emp.name.toLowerCase(),
            emp.employeeNumber.toLowerCase(),
            emp.designation.toLowerCase(),
            emp.pin?.toLowerCase() || '',
            emp.email?.toLowerCase() || '',
            emp.phone?.toLowerCase() || ''
          ];
          return searchableFields.some(field => field.includes(searchTerm));
        });
      }

      allEmployees.sort((a, b) => a.name.localeCompare(b.name));

      let employees: Employee[];
      let hasMore = false;
      let lastDocResult: QueryDocumentSnapshot<DocumentData> | undefined;

      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        employees = allEmployees;
        hasMore = false;
      } else {
        const docs = snapshot.docs;
        hasMore = docs.length > pageSize;
        const employeeDocs = hasMore ? docs.slice(0, pageSize) : docs;
        lastDocResult = employeeDocs.length > 0 ? employeeDocs[employeeDocs.length - 1] : undefined;
        employees = allEmployees.slice(0, pageSize);
      }

      return { employees, hasMore, lastDoc: lastDocResult };

    } catch (error) {
      throw new Error(`Failed to fetch employees: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getAllEmployeesForExport(): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      
      let allEmployees: Employee[] = [];
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let hasMore = true;

      while (hasMore) {
        let batchQuery = query(
          employeesRef,
          orderBy('__name__'),
          limit(this.BATCH_SIZE)
        );
        
        if (lastDoc) {
          batchQuery = query(batchQuery, startAfter(lastDoc));
        }

        const snapshot = await getDocs(batchQuery);
        
        if (snapshot.empty || snapshot.docs.length === 0) {
          hasMore = false;
          break;
        }

        const batchEmployees = snapshot.docs.map(doc => {
          try {
            return this.mapEmployeeData(doc as QueryDocumentSnapshot<DocumentData>);
          } catch (mappingError) {
            return {
              id: doc.id,
              name: doc.data().employeeName || doc.data().name || 'Unknown',
              employeeNumber: doc.id,
              designation: doc.data().designation || 'Unknown',
              department: 'Not Specified',
              email: doc.data().email || '',
              phone: doc.data().phone || doc.data().phoneNumber || '',
              joinDate: doc.data().joinDate || doc.data().joiningDate || '',
              status: 'Active' as const,
              avatar: '/images/humanicon.png',
              pin: doc.id.replace(/^EMP/i, ''),
              salary: doc.data().salary || 0
            };
          }
        });

        allEmployees.push(...batchEmployees);
        
        hasMore = snapshot.docs.length === this.BATCH_SIZE;
        lastDoc = snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData>;
      }

      allEmployees.sort((a, b) => a.name.localeCompare(b.name));
      
      return allEmployees;

    } catch (error) {
      throw new Error(`Failed to fetch all employees for export: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getEmployeeStats(): Promise<EmployeeStats> {
    try {
      // Check enhanced cache first
      if (this._statsCache && Date.now() - this._statsCache.timestamp < this.CACHE_TTL) {
        return this._statsCache.data;
      }

      const cacheKey = 'employee_stats';
      const cached = this.getFromCache<EmployeeStats>(cacheKey);
      if (cached) return cached;

      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      
      const designations = new Set<string>();
      let totalEmployees = 0;
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let hasMore = true;

      while (hasMore) {
        let batchQuery = query(
          employeesRef,
          orderBy('__name__'),
          limit(this.BATCH_SIZE)
        );
        
        if (lastDoc) {
          batchQuery = query(batchQuery, startAfter(lastDoc));
        }

        const snapshot = await getDocs(batchQuery);
        
        if (snapshot.empty || snapshot.docs.length === 0) {
          hasMore = false;
          break;
        }

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.designation && data.designation.trim() !== '') {
            designations.add(data.designation);
          }
        });

        totalEmployees += snapshot.docs.length;
        hasMore = snapshot.docs.length === this.BATCH_SIZE;
        lastDoc = snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData>;
      }
      
      const stats: EmployeeStats = {
        totalEmployees,
        activeEmployees: totalEmployees,
        inactiveEmployees: 0,
        onLeaveEmployees: 0,
        totalDepartments: 1,
        totalDesignations: designations.size
      };

      this.setCache(cacheKey, stats);
      this._statsCache = { data: stats, timestamp: Date.now() };
      
      return stats;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to calculate employee statistics');
    }
  }

  async getLineManagers(): Promise<any[]> {
    try {
      // Check enhanced cache first
      if (this._lineManagersCache && Date.now() - this._lineManagersCache.timestamp < this.CACHE_TTL) {
        return this._lineManagersCache.data;
      }

      const cacheKey = 'line_managers';
      const cached = this.getFromCache<any[]>(cacheKey);
      if (cached) {
        return cached;
      }

      const lineManagersRef = collection(db, 'line_managers');
      const snapshot = await getDocs(lineManagersRef);
      
      if (snapshot.empty) {
        return [];
      }

      const lineManagers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      this.setCache(cacheKey, lineManagers);
      this._lineManagersCache = { data: lineManagers, timestamp: Date.now() };
      
      return lineManagers;

    } catch (error) {
      return [];
    }
  }

  async getDesignations(): Promise<string[]> {
    try {
      // Check enhanced cache first
      if (this._designationsCache && Date.now() - this._designationsCache.timestamp < this.CACHE_TTL) {
        return this._designationsCache.data;
      }

      const cacheKey = 'designations_list';
      const cached = this.getFromCache<string[]>(cacheKey);
      if (cached) return cached;

      const summaries = await this.getDesignationSummaries();
      const designationsList = summaries.map(s => s.designation).sort();
      
      this.setCache(cacheKey, designationsList);
      this._designationsCache = { data: designationsList, timestamp: Date.now() };
      
      return designationsList;
    } catch (error) {
      return [];
    }
  }

  async getEmployeeById(employeeId: string): Promise<Employee | null> {
    try {
      const employeeDoc = doc(db, 'MasterSheet', 'Employee-Data', 'employees', employeeId);
      const snapshot = await getDoc(employeeDoc);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      return this.mapEmployeeData(snapshot as QueryDocumentSnapshot<DocumentData>);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch employee');
    }
  }

  // =====================
  // IMPORT FUNCTIONALITY
  // =====================

  async importEmployees(
    config: ImportConfig,
    onProgress?: (progress: ImportProgress) => void
  ): Promise<ImportResult> {
    try {
      if (config.monthlyUpdate && !config.validateOnly) {
        const monthlyInfo = await this.getMonthlyUpdateInfo(
          config.monthlyUpdate.month,
          config.monthlyUpdate.year
        );
        
        if (!monthlyInfo.canUpdate) {
          throw new Error(`Monthly update limit reached for ${config.monthlyUpdate.month}/${config.monthlyUpdate.year}.`);
        }
      }
      
      onProgress?.({
        current: 0,
        total: 100,
        status: 'reading',
        message: 'Reading uploaded file...'
      });

      const data = await this.readImportFile(config.file);
      const rows = data.slice(1);
      const headers = data[0];

      onProgress?.({
        current: 20,
        total: 100,
        status: 'validating',
        message: 'Validating data...'
      });

      const headerValidation = this.validateHeaders(headers);
      if (!headerValidation.valid) {
        throw new Error(`Invalid headers: ${headerValidation.errors.join(', ')}`);
      }

      const result = await this.processImportData(rows, headers, config, onProgress);

      if (!config.validateOnly) {
        onProgress?.({
          current: 90,
          total: 100,
          status: 'saving',
          message: 'Saving to database...'
        });

        await this.saveImportedEmployees(result.newEmployees, result.updatedEmployees, config);
        
        if (config.monthlyUpdate && result.success) {
          await this.recordMonthlyUpdate(
            config.monthlyUpdate.month,
            config.monthlyUpdate.year,
            config.monthlyUpdate.status,
            result.processedRows,
            'import'
          );
        }
      }

      onProgress?.({
        current: 100,
        total: 100,
        status: 'complete',
        message: config.monthlyUpdate 
          ? `Import completed for ${config.monthlyUpdate.month}/${config.monthlyUpdate.year}!`
          : 'Import completed successfully!'
      });

      return result;

    } catch (error) {
      onProgress?.({
        current: 0,
        total: 100,
        status: 'error',
        message: `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      
      return {
        success: false,
        totalRows: 0,
        processedRows: 0,
        errors: [{ row: 0, message: error instanceof Error ? error.message : 'Import failed' }],
        warnings: [],
        newEmployees: [],
        updatedEmployees: []
      };
    }
  }

  private async readImportFile(file: File): Promise<any[][]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          let jsonData: any[][];

          if (file.name.toLowerCase().endsWith('.csv')) {
            const text = data as string;
            const lines = text.split('\n').filter(line => line.trim() !== '');
            jsonData = lines.map(line => {
              const cells: string[] = [];
              let current = '';
              let inQuotes = false;
              
              for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                  inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                  cells.push(current.trim());
                  current = '';
                } else {
                  current += char;
                }
              }
              cells.push(current.trim());
              return cells;
            });
          } else {
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          }

          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));

      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  }

  private validateHeaders(headers: string[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const normalizedHeaders = headers.map(h => h.toLowerCase().replace(/\s+/g, ''));

    for (const required of REQUIRED_COLUMNS) {
      const found = normalizedHeaders.some(h => 
        h === required || COLUMN_MAPPING[h] === COLUMN_MAPPING[required]
      );
      if (!found) {
        errors.push(`Missing required column: ${required}`);
      }
    }

    return { valid: errors.length === 0, errors };
  }

  private async processImportData(
    rows: any[][],
    headers: string[],
    config: ImportConfig,
    onProgress?: (progress: ImportProgress) => void
  ): Promise<ImportResult> {
    const errors: ImportError[] = [];
    const warnings: ImportWarning[] = [];
    const newEmployees: Employee[] = [];
    const updatedEmployees: Employee[] = [];

    const existingEmployees = await this.getAllEmployeesMap();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 2;

      onProgress?.({
        current: 20 + (i / rows.length) * 60,
        total: 100,
        status: 'processing',
        message: `Processing row ${rowNumber}...`
      });

      try {
        const employee = this.mapRowToEmployee(row, headers, rowNumber);
        
        if (employee) {
          const existing = existingEmployees.get(employee.employeeNumber);
          
          if (existing) {
            if (config.updateExisting) {
              const updated = { ...existing, ...employee };
              updatedEmployees.push(updated);
            } else {
              warnings.push({
                row: rowNumber,
                message: `Employee ${employee.employeeNumber} already exists and will be skipped`
              });
            }
          } else {
            newEmployees.push(employee);
          }
        }
      } catch (error) {
        errors.push({
          row: rowNumber,
          message: error instanceof Error ? error.message : 'Processing error',
          data: row
        });
      }
    }

    return {
      success: errors.length === 0,
      totalRows: rows.length,
      processedRows: newEmployees.length + updatedEmployees.length,
      errors,
      warnings,
      newEmployees,
      updatedEmployees
    };
  }

  private mapRowToEmployee(row: any[], headers: string[], rowNumber: number): Employee | null {
    const employee: Partial<Employee> = {};
    const additionalFields: Record<string, any> = {};

    for (let j = 0; j < headers.length; j++) {
      const header = headers[j].toLowerCase().replace(/\s+/g, '');
      const value = row[j]?.toString().trim();
      
      if (!value) continue;

      const mappedField = COLUMN_MAPPING[header];
      
      if (mappedField) {
        if (mappedField === 'joinDate' && value) {
          employee[mappedField] = this.parseDate(value);
        } else {
          (employee as any)[mappedField] = value;
        }
      } else {
        additionalFields[headers[j]] = value;
      }
    }

    if (!employee.name || !employee.employeeNumber || !employee.designation) {
      throw new Error(`Missing required fields in row ${rowNumber}`);
    }

    const employeeId = employee.employeeNumber.startsWith('EMP') 
      ? employee.employeeNumber 
      : `EMP${employee.employeeNumber.padStart(4, '0')}`;

    return {
      id: employeeId,
      name: employee.name,
      employeeNumber: employeeId,
      designation: employee.designation,
      department: 'Not Specified',
      email: employee.email || '',
      phone: employee.phone || '',
      joinDate: employee.joinDate || '',
      status: 'Active',
      avatar: '/images/humanicon.png',
      pin: employee.employeeNumber,
      salary: 0,
      passportNumber: employee.passportNumber || '',
      passportExpiry: employee.passportExpiry || '',
      workEmail: employee.workEmail || '',
      ...additionalFields
    };
  }

  private parseDate(dateStr: string): string {
    try {
      // Handle Excel serial date numbers
      if (!isNaN(Number(dateStr))) {
        const excelDate = XLSX.SSF.parse_date_code(Number(dateStr));
        // Use local date components directly - NO timezone conversion
        const year = excelDate.y;
        const month = String(excelDate.m).padStart(2, '0');
        const day = String(excelDate.d).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      // Handle string dates
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        // Use LOCAL date methods instead of toISOString() to avoid timezone shift
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      return dateStr;
    } catch {
      return dateStr;
    }
  }

  private async getAllEmployeesMap(): Promise<Map<string, Employee>> {
    const { employees } = await this.getEmployees({ pageSize: this.MAX_FIREBASE_LIMIT });
    const map = new Map<string, Employee>();
    
    employees.forEach(emp => {
      map.set(emp.employeeNumber, emp);
    });
    
    return map;
  }

  private async saveImportedEmployees(
    newEmployees: Employee[],
    updatedEmployees: Employee[],
    config: ImportConfig
  ): Promise<void> {
    const batch = writeBatch(db);
    const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');

    for (const employee of newEmployees) {
      const docRef = doc(employeesRef, employee.id);
      const saveData: Record<string, any> = {
        employeeName: employee.name,
        designation: employee.designation,
        email: employee.email,
        phone: employee.phone,
        joinDate: employee.joinDate,
        passportNumber: employee.passportNumber,
        passportExpiry: employee.passportExpiry,
        workEmail: employee.workEmail
      };
      
      for (const key of Object.keys(employee)) {
        if (!EXCLUDED_SAVE_FIELDS.has(key)) {
          saveData[key] = employee[key];
        }
      }
      
      batch.set(docRef, saveData);
    }

    for (const employee of updatedEmployees) {
      const docRef = doc(employeesRef, employee.id);
      const updateData: Record<string, any> = {
        employeeName: employee.name,
        designation: employee.designation,
        email: employee.email,
        phone: employee.phone,
        joinDate: employee.joinDate,
        passportNumber: employee.passportNumber,
        passportExpiry: employee.passportExpiry,
        workEmail: employee.workEmail
      };
      
      for (const key of Object.keys(employee)) {
        if (!EXCLUDED_SAVE_FIELDS.has(key)) {
          updateData[key] = employee[key];
        }
      }
      
      batch.set(docRef, updateData, { merge: true });
    }

    await batch.commit();
  }

  // =====================
  // DOCUMENT SUPPORT METHODS
  // =====================

  async updateEmployeeFromDocument(
    employeeId: string, 
    extractedData: any,
    documentType: 'emirates-id' | 'passport' | 'visa' | 'labour-card'
  ): Promise<Employee> {
    try {
      const existingEmployee = await this.getEmployeeById(employeeId);
      if (!existingEmployee) {
        throw new Error(`Employee with ID ${employeeId} not found`);
      }

      const updateData: any = {
        [`${documentType}UpdatedAt`]: new Date(),
        documentLastUpdated: new Date()
      };

      switch (documentType) {
        case 'emirates-id':
          if (extractedData.emiratesIdNumber) {
            updateData['Emirates ID No.'] = extractedData.emiratesIdNumber;
          }
          if (extractedData.expiryDate) {
            updateData['Emirates ID Expiry Date'] = this.parseDocumentDate(extractedData.expiryDate);
          }
          if (extractedData.dateOfBirth) {
            updateData['Date of Birth'] = this.parseDocumentDate(extractedData.dateOfBirth);
          }
          if (extractedData.fullNameEnglish) {
            updateData.preferredName = extractedData.fullNameEnglish;
          }
          if (extractedData.nationality) {
            updateData.nationality = extractedData.nationality;
          }
          if (extractedData.gender) {
            updateData.gender = extractedData.gender;
          }
          break;

        case 'passport':
          if (extractedData.passportNumber) {
            updateData.passportNumber = extractedData.passportNumber;
          }
          if (extractedData.expiryDate) {
            updateData.passportExpiry = this.parseDocumentDate(extractedData.expiryDate);
          }
          if (extractedData.dateOfBirth) {
            updateData['Date of Birth'] = this.parseDocumentDate(extractedData.dateOfBirth);
          }
          if (extractedData.fullName) {
            updateData.preferredName = extractedData.fullName;
          }
          if (extractedData.nationality) {
            updateData.nationality = extractedData.nationality;
          }
          if (extractedData.gender) {
            updateData.gender = extractedData.gender;
          }
          break;

        case 'visa':
          if (extractedData.visaFileNumber) {
            updateData['Residency Visa File Number'] = extractedData.visaFileNumber;
          }
          if (extractedData.visaUID) {
            updateData['Residency Visa UID'] = extractedData.visaUID;
          }
          if (extractedData.expiryDate) {
            updateData['Residency Visa Expiry Date'] = this.parseDocumentDate(extractedData.expiryDate);
          }
          if (extractedData.fullName) {
            updateData.preferredName = extractedData.fullName;
          }
          if (extractedData.sponsorName) {
            updateData.sponsorName = extractedData.sponsorName;
          }
          break;

        case 'labour-card':
          if (extractedData.labourCardNumber) {
            updateData.labourCardNumber = extractedData.labourCardNumber;
          }
          if (extractedData.expiryDate) {
            updateData.labourCardExpiry = this.parseDocumentDate(extractedData.expiryDate);
          }
          if (extractedData.fullName) {
            updateData.preferredName = extractedData.fullName;
          }
          if (extractedData.employerName) {
            updateData.employerName = extractedData.employerName;
          }
          if (extractedData.jobTitle) {
            updateData.designation = extractedData.jobTitle;
          }
          if (extractedData.salary) {
            const salaryAmount = parseFloat(extractedData.salary);
            if (!isNaN(salaryAmount)) {
              updateData.salary = salaryAmount;
            }
          }
          break;
      }

      if (Object.keys(updateData).length <= 2) {
        return existingEmployee;
      }

      const updatedEmployee = await this.updateEmployee(employeeId, updateData);
      
      return updatedEmployee;
      
    } catch (error) {
      throw new Error(`Failed to update employee with document data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private parseDocumentDate(dateStr: string): string {
    try {
      let date: Date;
      
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          
          if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
            date = new Date(year, month - 1, day);
          } else {
            date = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
          }
        } else {
          date = new Date(dateStr);
        }
      } else if (dateStr.includes('-')) {
        date = new Date(dateStr);
      } else {
        date = new Date(dateStr);
      }
      
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      
      return date.toLocaleDateString('en-GB');
      
    } catch (error) {
      return dateStr;
    }
  }

  async getEmployeeDocumentHistory(employeeId: string): Promise<any[]> {
    try {
      const employee = await this.getEmployeeById(employeeId);
      if (!employee) {
        return [];
      }

      const history: any[] = [];
      const documentTypes = ['emirates-id', 'passport', 'visa', 'labour-card'];
      
      documentTypes.forEach(docType => {
        const timestampField = `${docType}UpdatedAt`;
        if ((employee as any)[timestampField]) {
          history.push({
            documentType: docType,
            updatedAt: (employee as any)[timestampField],
            status: 'updated'
          });
        }
      });

      history.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      
      return history;
      
    } catch (error) {
      return [];
    }
  }

  async getEmployeesWithMissingDocuments(): Promise<{
    employeeId: string;
    name: string;
    missingDocuments: string[];
  }[]> {
    try {
      const requiredFields = {
        'emirates-id': ['Emirates ID No.', 'Emirates ID Expiry Date'],
        'passport': ['passportNumber', 'passportExpiry'],
        'visa': ['Residency Visa File Number', 'Residency Visa Expiry Date'],
        'labour-card': ['labourCardNumber']
      };

      const { employees } = await this.getEmployees({ pageSize: this.MAX_FIREBASE_LIMIT });
      const employeesWithMissingDocs: any[] = [];

      employees.forEach(employee => {
        const missingDocuments: string[] = [];

        Object.entries(requiredFields).forEach(([docType, fields]) => {
          const hasSomeFields = fields.some(field => {
            const value = (employee as any)[field];
            return value && value !== '' && value !== 'Not specified';
          });

          if (!hasSomeFields) {
            missingDocuments.push(docType);
          }
        });

        if (missingDocuments.length > 0) {
          employeesWithMissingDocs.push({
            employeeId: employee.id,
            name: employee.name,
            missingDocuments
          });
        }
      });

      return employeesWithMissingDocs;
      
    } catch (error) {
      return [];
    }
  }

  async getDocumentExpiryAlerts(daysAhead: number = 90): Promise<{
    employeeId: string;
    name: string;
    designation: string;
    expiringDocuments: {
      type: string;
      expiryDate: string;
      daysUntilExpiry: number;
    }[];
  }[]> {
    try {
      const { employees } = await this.getEmployees({ pageSize: this.MAX_FIREBASE_LIMIT });
      const alerts: any[] = [];
      const now = new Date();
      const futureDate = new Date(now.getTime() + (daysAhead * 24 * 60 * 60 * 1000));

      employees.forEach(employee => {
        const expiringDocuments: any[] = [];

        const documentFields = {
          'Emirates ID': (employee as any)['Emirates ID Expiry Date'],
          'Passport': employee.passportExpiry,
          'Residence Visa': (employee as any)['Residency Visa Expiry Date'],
          'Labour Card': (employee as any).labourCardExpiry
        };

        Object.entries(documentFields).forEach(([docType, expiryDateStr]) => {
          if (expiryDateStr && expiryDateStr !== 'Not specified') {
            try {
              const expiryDate = new Date(expiryDateStr);
              
              if (!isNaN(expiryDate.getTime()) && expiryDate <= futureDate && expiryDate >= now) {
                const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
                
                expiringDocuments.push({
                  type: docType,
                  expiryDate: expiryDate.toLocaleDateString('en-GB'),
                  daysUntilExpiry
                });
              }
            } catch (error) {
              // Silent error handling
            }
          }
        });

        if (expiringDocuments.length > 0) {
          alerts.push({
            employeeId: employee.id,
            name: employee.name,
            designation: employee.designation,
            expiringDocuments: expiringDocuments.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
          });
        }
      });

      alerts.sort((a, b) => {
        const aMinDays = Math.min(...a.expiringDocuments.map((doc: any) => doc.daysUntilExpiry));
        const bMinDays = Math.min(...b.expiringDocuments.map((doc: any) => doc.daysUntilExpiry));
        return aMinDays - bMinDays;
      });

      return alerts;
      
    } catch (error) {
      return [];
    }
  }

  // =====================
  // UTILITY METHODS
  // =====================

  clearCache(): void {
    this.sessionCache.clear();
    this._statsCache = null;
    this._designationsCache = null;
    this._lineManagersCache = null;
  }

  cleanup(): void {
    this.activeListeners.forEach(unsubscribe => unsubscribe());
    this.activeListeners.clear();
    this.clearCache();
  }

  async testConnection(): Promise<{ success: boolean; message: string; latency?: number; docCount?: number }> {
    const startTime = Date.now();
    
    try {
      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const testQuery = query(employeesRef, limit(5));
      const snapshot = await getDocs(testQuery);
      
      const latency = Date.now() - startTime;
      const docCount = snapshot.size;
      
      return {
        success: true,
        message: `Employee service connection successful - found ${docCount} documents`,
        latency,
        docCount
      };
    } catch (error) {
      const latency = Date.now() - startTime;
      return {
        success: false,
        message: `Employee service connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        latency,
        docCount: 0
      };
    }
  }

  async debugDataStructure(): Promise<void> {
    try {
      const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      await getDocs(query(employeesRef, limit(3)));
    } catch (error) {
      // Silent error handling
    }
  }
}

export const employeeService = new EmployeeService();